import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import SignatureDialog from '@/components/SignatureDialog';
import { CharteTexte, useCharteInfo } from '@/pages/Charte';
import { ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sur un compte de site partagé, un agent qui n'a pas encore signé la charte
// (sur son compte personnel) doit la lire et la signer après avoir saisi son
// code personnel. Le serveur répond 403 CHARTE_REDACTEUR_REQUISE à toute action
// faite au nom de cet agent (saisie d'un événement, signature d'une consigne,
// compte rendu, etc.) ; ce composant intercepte cette réponse, affiche la
// charte à signer, enregistre la signature sur le compte personnel de l'agent,
// puis rejoue automatiquement l'action interrompue.
const CharteRedacteurGate = () => {
  const [pending, setPending] = useState(null);
  const origFetchRef = useRef(null);

  useEffect(() => {
    const origFetch = window.fetch.bind(window);
    origFetchRef.current = origFetch;
    window.fetch = async (input, init) => {
      const res = await origFetch(input, init);
      if (res.status !== 403) return res;
      let detail;
      try { detail = (await res.clone().json()).detail; } catch { return res; }
      if (!detail || detail.code !== 'CHARTE_REDACTEUR_REQUISE') return res;
      let body = {};
      try { body = JSON.parse(init?.body); } catch { /* corps non JSON */ }
      const pin = body.redacteur_pin || body.pin;
      if (!detail.redacteur_id || !pin) {
        return new Response(JSON.stringify({ detail: detail.message }), { status: 403, headers: { 'Content-Type': 'application/json' } });
      }
      return new Promise((resolve) => {
        setPending({
          name: detail.redacteur_name,
          redacteurId: detail.redacteur_id,
          pin,
          done: async () => { setPending(null); resolve(await origFetch(input, init)); },
          cancel: () => {
            setPending(null);
            resolve(new Response(JSON.stringify({ detail: "La charte doit être signée par l'agent avant de continuer" }),
              { status: 403, headers: { 'Content-Type': 'application/json' } }));
          },
        });
      });
    };
    return () => { window.fetch = origFetch; };
  }, []);

  if (!pending) return null;
  return <CharteRedacteurDialog pending={pending} origFetch={origFetchRef.current} />;
};

const CharteRedacteurDialog = ({ pending, origFetch }) => {
  const { info, error } = useCharteInfo();
  const texteRef = useRef(null);
  const [checked, setChecked] = useState(false);
  const [signature, setSignature] = useState(null);
  const [signing, setSigning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validate = async () => {
    setSubmitting(true);
    try {
      const res = await origFetch(`${BACKEND_URL}/api/auth/charte-redacteur`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          redacteur_id: pending.redacteurId,
          redacteur_pin: pending.pin,
          signature,
          texte: texteRef.current?.innerText || '',
        }),
      });
      if (res.ok) {
        toast.success(`Charte signée. Le compte personnel de ${pending.name} est à jour.`);
        await pending.done();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Erreur lors de la signature');
      }
    } catch {
      toast.error('Erreur de connexion au serveur');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Dialog open onOpenChange={() => {}}>
        <DialogContent
          className="max-w-3xl max-h-[92vh] flex flex-col [&>button]:hidden"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          data-testid="charte-redacteur-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
              Charte à signer — {pending.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {pending.name} n'a pas encore signé la charte d'utilisation et de protection des données (ou une nouvelle version est parue).
            Votre code personnel est confirmé : merci de lire la charte puis de la signer. Votre signature est enregistrée sur
            votre compte personnel, vous n'aurez pas à la refaire.
          </p>
          <div ref={texteRef} className="overflow-y-auto flex-1 border rounded-lg p-4 bg-slate-50 dark:bg-slate-950 min-h-[160px]" data-testid="charte-redacteur-text">
            {error && <p className="text-red-600">Impossible de charger la charte.</p>}
            <CharteTexte info={info} />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox id="charte-red-checkbox" checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="charte-redacteur-checkbox" />
            <label htmlFor="charte-red-checkbox" className="text-sm cursor-pointer">
              J'ai lu et compris la charte et l'information sur mes données personnelles, et j'en accepte les règles.
            </label>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            {signature ? (
              <div className="flex items-center gap-3">
                <img src={signature} alt="signature" className="h-12 border rounded bg-white" />
                <Button variant="outline" size="sm" onClick={() => setSigning(true)}>Refaire</Button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setSigning(true)} data-testid="charte-redacteur-sign-btn">Signer à main levée</Button>
            )}
            <Button className="btn-primary flex-1" onClick={validate} disabled={!checked || !signature || submitting || !info} data-testid="charte-redacteur-validate">
              {submitting ? 'Enregistrement…' : 'Valider et signer la charte'}
            </Button>
            <Button variant="ghost" onClick={pending.cancel} disabled={submitting} data-testid="charte-redacteur-cancel">Annuler</Button>
          </div>
        </DialogContent>
      </Dialog>
      <SignatureDialog
        open={signing}
        onClose={() => setSigning(false)}
        onSign={(sig) => { setSignature(sig); setSigning(false); }}
        title={`Signature de la charte — ${pending.name}`}
      />
    </>
  );
};

export default CharteRedacteurGate;
